/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { GroupTypes } from "~/generated/graphql";
import { dateFormat } from "~/constants";
import { createCountFilterDefinition } from "~/hooks/useAppliedFilters/definitionFactories";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { GROUP_TYPE_LABELS } from "~/views/resourceGroups/constants";
import { ResourceGroupQuery } from "~/views/resourceGroups/types";

type DefinitionKey = "creationDate" | "types";

type ResourceGroupQueryState = ResourceGroupQuery & QueryState;

type Definition = AppliedFilterDefinition<
  ResourceGroupQueryState,
  DefinitionKey
>;

export const resourceGroupsAppliedFilters: Definition[] = [
  {
    key: "creationDate",
    label: "Creation Date",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.startDateUTC && queryState.endDateUTC
        ? [queryState.startDateUTC, queryState.endDateUTC]
        : [],
    getValueDisplay: (queryState) =>
      `${dayjs(queryState.startDateUTC).format(dateFormat.shortDate)} - ${dayjs(
        queryState.endDateUTC
      ).format(dateFormat.shortDate)}`,
    clear: () => ({
      startDateUTC: undefined,
      endDateUTC: undefined,
    }),
  },
  createCountFilterDefinition<ResourceGroupQueryState, "types">({
    key: "types",
    label: "Type",
    labels: { singular: "Type", plural: "Types" },
    getValues: (queryState) => queryState.types,
    getExcluded: (queryState) => queryState.excludeTypes,
    getValueLabel: (value) => GROUP_TYPE_LABELS[value as GroupTypes] ?? value,
    clear: () => ({ types: [], excludeTypes: false }),
    applyValuesPatch: (queryState, values) => ({
      types: values as ResourceGroupQuery["types"],
      excludeTypes: values.length ? queryState.excludeTypes : false,
    }),
  }),
];
